import { useMemo, useState } from 'react';
import { Theme, Tooltip, Typography, useMediaQuery } from '@mui/material';

import styles from './FirstLoginTooltip.module.scss';
import classnames from 'classnames';

type FirstLoginTooltipProps = {
  children: JSX.Element;
  className?: string;
  arrowClassName?: string;
  anchorEl?: HTMLElement | null;
  popperOffset?: [number, number];
  open: boolean;
  text: string;
  numberText?: string;
  actionText?: string;
  placement?:
    | 'bottom-end'
    | 'bottom-start'
    | 'bottom'
    | 'left-end'
    | 'left-start'
    | 'left'
    | 'right-end'
    | 'right-start'
    | 'right'
    | 'top-end'
    | 'top-start'
    | 'top';
  onClick(): Promise<void>;
};

export function FirstLoginTooltip({
  children,
  text,
  numberText,
  actionText,
  open,
  className,
  arrowClassName,
  anchorEl,
  popperOffset,
  placement,
  onClick,
}: FirstLoginTooltipProps) {
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const [loading, setLoading] = useState<boolean>(false);
  const progress = useMemo(() => {
    if (!numberText) {
      return null;
    }

    const match = numberText.match(/(\d+)\s*of\s*(\d+)/i);
    if (!match) {
      return null;
    }

    const current = Number(match[1]);
    const total = Number(match[2]);
    if (!Number.isFinite(current) || !Number.isFinite(total) || total <= 0) {
      return null;
    }

    const percent = Math.min(100, Math.max(0, Math.round((current / total) * 100)));
    return {
      label: `Step ${current} of ${total}`,
      percent,
    };
  }, [numberText]);

  const handleClick = () => {
    if (loading) {
      return;
    }

    setLoading(true);
    onClick().finally(() => setLoading(false));
  };

  const tooltipPopperProps: any = {
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: popperOffset ?? [0, 8],
        },
      },
    ],
  };

  if (anchorEl) {
    tooltipPopperProps.anchorEl = anchorEl;
  }

  return (
    <>
      <Tooltip
        title={
          <div className={styles.container}>
            <Typography component='p' variant='body5'>
              {text}
            </Typography>

            <div className={styles.navigation}>
              <div className={styles.progressInfo}>
                {progress ? (
                  <>
                    <span className={styles.stepBadge}>{progress.label}</span>
                    <div className={styles.progressTrack}>
                      <div className={styles.progressFill} style={{ width: `${progress.percent}%` }} />
                    </div>
                  </>
                ) : (
                  <Typography
                    component='p'
                    variant={isDesktop ? 'subtitle2' : 'body7'}
                    color='var(--app-brand-primary)'
                  >
                    {numberText}
                  </Typography>
                )}
              </div>

              <button className={styles.actionButton} onClick={handleClick} disabled={loading} type='button'>
                {actionText ?? 'Next'}
              </button>
            </div>
          </div>
        }
        placement={placement ?? 'bottom-end'}
        arrow
        classes={{
          tooltip: classnames(styles.tooltip, className),
          arrow: classnames(styles.arrowTooltip, arrowClassName),
        }}
        PopperProps={tooltipPopperProps}
        open={open}
      >
        {children}
      </Tooltip>

      {open && <div className={styles.bg}></div>}
    </>
  );
}
